import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { CheckCircle } from 'phosphor-react';
import SBack from '@/components/ui/molecules/SBack';
import SLoader from '@/components/ui/atoms/SLoader';
import { SButton } from '@/components/ui/atoms/SButton';
import { ThemeToggle } from '@/components/ui/atoms/ThemeToggle';
import UpdatePaymentMethodForm from '@/components/ui/organisms/UpdatePaymentMethodForm';
import { useUserLocation } from '@/hooks/useUserLocation';
import { useUpdatePaymentLink } from '@/hooks/useUpdatePaymentLink';
import memberService from '@/services/member.service';
import { updateMemberStatus } from '@/store/authSlice';
import { MemberStatus } from '@/types/member.types';
import { logger } from '@/utils/logger';
import type { RootState } from '@/store';
import './UpdatePaymentMethod.css';

/** How long the success screen stays up before we drop the member back on home. */
const REDIRECT_DELAY_MS = 1500;

const UpdatePaymentMethod: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector((state: RootState) => state.auth.user);
  const { location, isLoading: isLocationLoading } = useUserLocation();
  const [isSuccess, setIsSuccess] = useState(false);
  const redirectTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  /**
   * The club's hosted billing page, read live from the CRM. Present
   * once the club has been migrated; absent clubs keep the in-app
   * Stripe form below.
   */
  const { data: paymentLink, isLoading: isLinkLoading } =
    useUpdatePaymentLink();
  const billingUrl = paymentLink?.updatePaymentLink;

  /**
   * Whether the member has been sent out to the billing page yet.
   *
   * The page is external and opaque to us — no callback, no redirect
   * back — so the screen has two states: an invitation to go, and,
   * once they have been, a confirmation step to come back to. The
   * flag lives only for this visit; a member who leaves the screen
   * starts from the invitation again.
   */
  const [hasOpenedBilling, setHasOpenedBilling] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);

  /**
   * Hard reload rather than a client-side navigate: home and every access guard
   * read the membership status that was cached when the app booted, so a full
   * reload is what actually shows the unblocked home screen.
   */
  const goHome = useCallback(() => {
    window.location.assign('/home');
  }, []);

  useEffect(
    () => () => {
      if (redirectTimer.current) clearTimeout(redirectTimer.current);
    },
    [],
  );

  const handlePaymentSaved = useCallback(async () => {
    setIsSuccess(true);

    try {
      // Backend writes the status to the CRM first, then to its own record.
      const profile = await memberService.reactivateAfterPaymentUpdate();
      dispatch(updateMemberStatus(profile.status ?? MemberStatus.ACTIVE));
    } catch (err) {
      // The card was saved either way — don't block the member on the sync.
      // A failed flip leaves them past_due and the gate reappears on home.
      logger.error('Failed to reactivate membership after payment update:', err);
    }

    redirectTimer.current = setTimeout(goHome, REDIRECT_DELAY_MS);
  }, [dispatch, goHome]);

  /* Same as every other outbound link in the app (FAQs, terms, banner
     CTAs). The native wrapper's `onOpenWindow` / `onShouldStartLoad`
     already hand any non-app origin to the system browser, so no
     bridge message is involved. */
  const openBillingPage = useCallback(() => {
    if (!billingUrl) return;
    window.open(billingUrl, '_blank', 'noopener,noreferrer');
    setHasOpenedBilling(true);
  }, [billingUrl]);

  /* Same call the Stripe form makes on success — the backend notifies
     ops and flips the status; see `activateAfterPaymentUpdate`. The
     member confirming is what stands in for the form's success event,
     which an external page cannot give us. */
  const confirmUpdated = useCallback(async () => {
    setIsConfirming(true);
    await handlePaymentSaved();
  }, [handlePaymentSaved]);

  if (isSuccess) {
    return (
      <div className="upm-page">
        <div className="upm-page__header">
          <ThemeToggle />
        </div>
        <div className="upm-page__success">
          <div className="upm-page__success-icon">
            <CheckCircle size={40} weight="fill" />
          </div>
          <h1 className="upm-page__title">{t('membership.pastDue.success.title')}</h1>
          <p className="upm-page__subtitle">{t('membership.pastDue.success.message')}</p>
          <SButton
            size="xl"
            variant="primary"
            className="upm-page__success-cta"
            label={t('membership.pastDue.success.cta')}
            onClick={goHome}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="upm-page">
      <div className="upm-page__header">
        <SBack onClick={() => navigate(-1)} />
        <ThemeToggle />
      </div>

      <div className="upm-page__body">
        <h1 className="upm-page__title">
          {billingUrl
            ? t('membership.pastDue.hosted.title')
            : t('membership.pastDue.form.title')}
        </h1>
        {/* Held back until the link resolves — the two flows explain
            themselves differently, and showing the wrong explanation
            for a beat is the one part of this the member reads. The
            title is the same string either way, so it can render
            immediately. */}
        {!isLinkLoading && (
          <p className="upm-page__subtitle">
            {billingUrl
              ? t('membership.pastDue.hosted.subtitle')
              : t('membership.pastDue.form.subtitle')}
          </p>
        )}

        {/* The link decides which of the two flows renders, so wait
            for it before showing either — flashing the Stripe form
            and replacing it a beat later is worse than a spinner. */}
        {isLocationLoading || isLinkLoading || !location || !user?.crmId ? (
          <div className="upm-page__loading">
            <SLoader />
          </div>
        ) : billingUrl ? (
          /* Hosted billing: the card is captured on the club's own
             page, outside the app entirely. We never see or handle
             the details. */
          <div className="upm-page__hosted">
            <SButton
              size="xl"
              variant="primary"
              className="upm-page__hosted-cta"
              label={
                hasOpenedBilling
                  ? t('membership.pastDue.hosted.reopen')
                  : t('membership.pastDue.hosted.cta')
              }
              onClick={openBillingPage}
            />

            {/* Only after they have actually been sent out. Offering
                "I've updated it" to someone who has not left the
                screen invites a tap that flips their status without a
                card ever being entered. */}
            {hasOpenedBilling && (
              <div className="upm-page__hosted-return">
                <h2 className="upm-page__hosted-return-title">
                  {t('membership.pastDue.hosted.returnTitle')}
                </h2>
                <p className="upm-page__subtitle">
                  {t('membership.pastDue.hosted.returnSubtitle')}
                </p>
                <SButton
                  size="xl"
                  variant="primary"
                  className="upm-page__hosted-cta"
                  label={
                    isConfirming
                      ? t('membership.pastDue.hosted.confirming')
                      : t('membership.pastDue.hosted.confirm')
                  }
                  disabled={isConfirming}
                  onClick={confirmUpdated}
                />
              </div>
            )}
          </div>
        ) : (
          /* No billing link for this club — not migrated yet, so the
             in-app Stripe form is still the way to pay. */
          <UpdatePaymentMethodForm
            memberId={user.crmId}
            locationId={location.id}
            email={user.email}
            onSuccess={handlePaymentSaved}
          />
        )}
      </div>
    </div>
  );
};

export default UpdatePaymentMethod;
